import { useState, useEffect, useRef } from 'react'
import { router, Link } from '@inertiajs/react'
import { ArrowLeft, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FileIcon } from '@/components/design-system'
import { formatNumber, formatCurrency } from '@/frontend/pages/CommandCenter/Telemetry/utils'
import { cn } from '@/lib/utils'
import SessionListItem from './SessionListItem'
import ConfigSidebarContent from './ConfigSidebarContent'
import PlanChangeSection from './PlanChangeSection'
import GrantCompedSubscriptionSection from './GrantCompedSubscriptionSection'
import type { Session, DigitalWorker, Pagination, Stats, ConfigData, AvailablePlan, CurrentPlan, BillingPlanOption, WorkerSubscription, PaymentLink } from '../types'
import type { KnowledgeData, KnowledgeTreeEntry, SelectedFile } from '@/frontend/components/DigitalWorkers/types'
import { matchesSearch, filterTree } from '@/frontend/components/DigitalWorkers/memoria/utils'

interface Props {
  digitalWorker: Pick<DigitalWorker, 'id' | 'name' | 'provisioningStatus' | 'runtimeCapabilities'>
  stats: Stats | null
  sessions: Session[]
  pagination: Pagination
  selectedSessionId: string | null
  search: string
  channel: string
  dateFrom: string
  dateTo: string
  channels: string[]
  basePath: string
  currentParams: Record<string, string | number | undefined>
  onSessionClick: (sessionId: string) => void
  activeTab?: 'sessoes' | 'rotinas' | 'memoria' | 'allowlist' | 'config'
  onTabChange?: (tab: 'sessoes' | 'rotinas' | 'memoria' | 'allowlist' | 'config') => void
  memorySearch?: string
  onMemorySearchChange?: (value: string) => void
  knowledge?: KnowledgeData | null
  onSelectFile?: (file: SelectedFile | null) => void
  config?: ConfigData | null
  currentPlan?: CurrentPlan
  availablePlans?: AvailablePlan[] | null
  billing?: WorkerSubscription
  billingPlans?: BillingPlanOption[]
  paymentLink?: PaymentLink | null
}

const PROVISIONING_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  provisioning: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  ready: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
}

const PROVISIONING_LABELS: Record<string, string> = {
  pending: 'Pendente',
  provisioning: 'Provisionando',
  ready: 'Online',
  failed: 'Falha',
}

function TreeNode({ entry, selectedPath, onSelect, depth = 0 }: {
  entry: KnowledgeTreeEntry; selectedPath: string | null; onSelect: (f: SelectedFile) => void; depth?: number
}) {
  const [expanded, setExpanded] = useState(depth < 2)
  if (entry.type === 'directory') {
    const children = entry.children ?? []
    if (children.length === 0) return null
    return (
      <div>
        <button type="button" className="flex items-center gap-1.5 w-full text-left py-1 px-2 text-sm text-foreground hover:bg-accent rounded"
          style={{ paddingLeft: `${depth * 12 + 8}px` }} onClick={() => setExpanded(!expanded)}>
          <span className="text-muted-foreground text-xs w-4 text-center">{expanded ? '▼' : '▶'}</span>
          <span className="font-medium">{entry.name}/</span>
        </button>
        {expanded && children.map((child) => (
          <TreeNode key={child.path} entry={child} selectedPath={selectedPath} onSelect={onSelect} depth={depth + 1} />
        ))}
      </div>
    )
  }
  return (
    <button type="button" style={{ paddingLeft: `${depth * 12 + 24}px` }}
      className={cn('flex items-center gap-1.5 w-full text-left py-1 px-2 text-sm rounded',
        selectedPath === entry.path ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400' : 'text-muted-foreground hover:bg-accent'
      )}
      onClick={() => onSelect({ name: entry.name, path: entry.path, content: entry.content ?? null })}>
      <FileIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="truncate">{entry.name}</span>
    </button>
  )
}

export default function SessionSidebar({
  digitalWorker, stats, sessions, pagination, selectedSessionId, search, channel,
  dateFrom, dateTo, channels, basePath, currentParams, onSessionClick,
  activeTab = 'sessoes', onTabChange, memorySearch = '', onMemorySearchChange, knowledge, onSelectFile, config,
  currentPlan, availablePlans, billing, billingPlans = [], paymentLink,
}: Props) {
  const [query, setQuery] = useState(search)
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null)
  const isReady = digitalWorker.provisioningStatus === 'ready'
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const navigateWithParams = (overrides: Record<string, string | number | undefined>) => {
    const params = { ...currentParams, ...overrides }
    const cleanParams = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
    router.get(basePath, cleanParams, { preserveState: true })
  }

  useEffect(() => {
    if (query === search) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      navigateWithParams({ search: query || undefined, page: 1, event_page: undefined })
    }, 400)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  const goToPage = (page: number) => navigateWithParams({ page, event_page: 1 })

  const handleFileSelect = (file: SelectedFile) => {
    setSelectedFilePath(file.path)
    onSelectFile?.(file)
  }

  const pillClass = (active: boolean) => cn(
    'px-3 py-1 text-xs font-medium rounded-full transition-colors',
    active ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400' : 'text-muted-foreground hover:text-accent-foreground'
  )

  // Keep the large memory tree out of render work until the memory tab is visible.
  const showMemory = activeTab === 'memoria'
  const bootstrapFiles = showMemory ? (knowledge?.bootstrap ?? []).filter((f) => f.exists) : []
  const knowledgeTree = showMemory ? knowledge?.knowledge ?? [] : []
  const filteredBootstrap = memorySearch ? bootstrapFiles.filter((f) => matchesSearch(memorySearch, f.name, f.content)) : bootstrapFiles
  const filteredTree = memorySearch ? filterTree(knowledgeTree, memorySearch) : knowledgeTree

  return (
    <div className="w-80 lg:w-96 border-r border-border flex flex-col h-full bg-background flex-shrink-0">
      {/* Header */}
      <div className="px-3 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Link href="/cc/digital_workers" className="text-muted-foreground hover:text-muted-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h3 className="text-sm font-semibold text-foreground truncate">{digitalWorker.name}</h3>
          {digitalWorker.provisioningStatus && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${PROVISIONING_COLORS[digitalWorker.provisioningStatus] || 'bg-accent text-foreground'}`}>
              {PROVISIONING_LABELS[digitalWorker.provisioningStatus] || digitalWorker.provisioningStatus}
            </span>
          )}
        </div>
        {availablePlans && (
          <PlanChangeSection workerId={digitalWorker.id} currentPlan={currentPlan ?? null} availablePlans={availablePlans} />
        )}
        {billing && (
          <GrantCompedSubscriptionSection
            workerId={digitalWorker.id}
            billing={billing}
            plans={billingPlans}
            paymentLink={paymentLink ?? null}
          />
        )}
        {isReady && onTabChange && (
          <div className="flex items-center gap-1 mt-2">
            <button onClick={() => onTabChange('sessoes')} className={pillClass(activeTab === 'sessoes')}>Sessões</button>
            <button onClick={() => onTabChange('rotinas')} className={pillClass(activeTab === 'rotinas')}>Rotinas</button>
            <button onClick={() => onTabChange('memoria')} className={pillClass(activeTab === 'memoria')}>Memória</button>
            {(digitalWorker.runtimeCapabilities.whatsapp_cloud.supported || digitalWorker.runtimeCapabilities.gateway_channels.supported) && <button onClick={() => onTabChange('allowlist')} className={pillClass(activeTab === 'allowlist')}>Allowlist</button>}
            <button onClick={() => onTabChange('config')} className={pillClass(activeTab === 'config')}>Config</button>
          </div>
        )}
        {activeTab === 'sessoes' && stats && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent text-muted-foreground">{formatNumber(stats.totalSessions)} sessões</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-400">{formatNumber(stats.activeSessions)} ativas</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400">{formatNumber(stats.totalTokens)} tokens</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-600">{formatCurrency(stats.totalCost)}</span>
          </div>
        )}
      </div>

      {showMemory ? (
        <>
          {/* Memory search */}
          <div className="px-3 py-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input type="text" value={memorySearch} onChange={(e) => onMemorySearchChange?.(e.target.value)}
                placeholder="Buscar em arquivos de memória..."
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
          </div>
          {/* File tree */}
          <div className="flex-1 overflow-y-auto p-3">
            {filteredBootstrap.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">Configuração</h3>
                {filteredBootstrap.map((file) => (
                  <button key={file.name} type="button"
                    className={cn('flex items-center gap-1.5 w-full text-left py-1 px-2 text-sm rounded',
                      selectedFilePath === file.name ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400' : 'text-muted-foreground hover:bg-accent'
                    )}
                    onClick={() => handleFileSelect({ name: file.name, path: file.name, content: file.content })}>
                    <FileIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{file.name}</span>
                  </button>
                ))}
              </div>
            )}
            {filteredTree.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-2">Base de Conhecimento</h3>
                {filteredTree.map((entry) => (
                  <TreeNode key={entry.path} entry={entry} selectedPath={selectedFilePath} onSelect={handleFileSelect} />
                ))}
              </div>
            )}
            {filteredBootstrap.length === 0 && filteredTree.length === 0 && (
              <div className="px-3 py-8 text-center text-xs text-muted-foreground">
                {memorySearch ? 'Nenhum arquivo encontrado.' : 'Nenhum arquivo de memória.'}
              </div>
            )}
          </div>
        </>
      ) : activeTab === 'sessoes' ? (
        <>
          {/* Session search + filters */}
          <div className="px-3 py-2 border-b border-border space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar sessões..."
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div className="flex items-center gap-1.5">
              <input type="date" value={dateFrom} onChange={(e) => navigateWithParams({ date_from: e.target.value || undefined, page: 1, event_page: 1 })}
                className="flex-1 min-w-0 text-[10px] border border-border rounded px-1.5 py-1 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring" />
              <span className="text-[10px] text-muted-foreground">a</span>
              <input type="date" value={dateTo} onChange={(e) => navigateWithParams({ date_to: e.target.value || undefined, page: 1, event_page: 1 })}
                className="flex-1 min-w-0 text-[10px] border border-border rounded px-1.5 py-1 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring" />
              {channels.length > 0 && (
                <select value={channel} onChange={(e) => navigateWithParams({ channel: e.target.value || undefined, page: 1, event_page: 1 })}
                  className="text-[10px] border border-border rounded px-1.5 py-1 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring">
                  <option value="">Todos</option>
                  {channels.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
                </select>
              )}
            </div>
          </div>
          {/* Session list */}
          <div className="flex-1 overflow-y-auto divide-y divide-border">
            {sessions.map((session) => (
              <SessionListItem key={session.id} session={session} isActive={session.sessionId === selectedSessionId}
                onClick={() => onSessionClick(session.sessionId)} />
            ))}
            {sessions.length === 0 && (
              <div className="px-3 py-8 text-center text-xs text-muted-foreground">Nenhuma sessão encontrada.</div>
            )}
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted flex-shrink-0">
              <span className="text-[10px] text-muted-foreground">{pagination.page}/{pagination.totalPages}</span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-6 w-6" disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)}>
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <Button variant="outline" size="icon" className="h-6 w-6" disabled={pagination.page >= pagination.totalPages} onClick={() => goToPage(pagination.page + 1)}>
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : activeTab === 'config' ? (
        <ConfigSidebarContent config={config ?? null} />
      ) : (
        <div className="flex-1" />
      )}

    </div>
  )
}
